import { useQueries } from "@tanstack/react-query";
import { CommonAPI, SessionTreeNode } from "../types";
import { sessionQueries } from "../queries";
import { Spinner, Table } from "react-bootstrap";
import SessionLink from "./SessionLink";
import { util } from "..";
import CloneTimeline from "./CloneTimeline";
import { Card, CardGrid } from "@bagel-web/components";

const ChildSessionRow = ({
  child,
  commonAPI,
  depth = 0,
}: {
  child: SessionTreeNode;
  commonAPI: CommonAPI;
  depth?: number;
}) => {
  return (
    <>
      <tr key={child.session_id}>
        <td>
          <div style={{ paddingLeft: `${depth * 20}px` }}>
            <SessionLink sessionId={child.session_id} commonAPI={commonAPI} />
          </div>
        </td>
        <td>{util.formatTimestamp(child.last_activity_at)}</td>
        <td>{util.formatTimestamp(child.created)}</td>
        <td>{child.origin_moment_id}</td>
      </tr>
      {child.children?.map((grandchild) => (
        <ChildSessionRow
          key={grandchild.session_id}
          child={grandchild}
          commonAPI={commonAPI}
          depth={depth + 1}
        />
      ))}
    </>
  );
};

const SessionCloneHistory = ({
  sessionId,
  commonAPI,
}: {
  sessionId?: string;
  commonAPI: CommonAPI;
}) => {
  if (!sessionId) {
    return null;
  }

  const [
    { data: sessionParents, isPending: isPendingParents },
    { data: sessionChildren, isPending: isPendingChildren },
  ] = useQueries({
    queries: [
      {
        queryKey: sessionQueries.getParents(sessionId),
        queryFn: () =>
          sessionId ? commonAPI.fetchSessionParents(sessionId) : undefined,
      },
      {
        queryKey: sessionQueries.getChildren(sessionId),
        queryFn: () =>
          sessionId ? commonAPI.fetchSessionChildren(sessionId) : undefined,
      },
    ],
  });

  const isPending = isPendingParents || isPendingChildren;

  if (isPending) {
    return <Spinner />;
  }

  const hasParents = sessionParents && sessionParents.length > 1;
  const hasChildren =
    sessionChildren?.children && sessionChildren?.children?.length > 0;

  if (!hasParents && !hasChildren) {
    return <div className="mt-3">Session has no parent or clones.</div>;
  }

  return (
    <CardGrid maxColumns={1} className="mt-3">
      {(hasParents || hasChildren) && (
        <Card title="Clone Timeline">
          <CloneTimeline
            sessionId={sessionId}
            sessionParents={sessionParents}
            sessionChildren={sessionChildren}
          />
        </Card>
      )}

      {hasParents && (
        <Card title="Ancestor Sessions">
          <Table className="mb-4">
            <thead>
              <tr>
                <th>Ancestor Session ID</th>
                <th>Last Activity</th>
                <th>Created</th>
                <th>First Moment</th>
                <th>Last Moment</th>
              </tr>
            </thead>
            <tbody>
              {sessionParents?.map((parent) => {
                // Exclude the current session from the list of ancestors
                return parent.session_id === sessionId ? null : (
                  <tr key={parent.session_id}>
                    <td>
                      <SessionLink
                        sessionId={parent.session_id}
                        commonAPI={commonAPI}
                      />
                    </td>
                    <td>{util.formatTimestamp(parent.last_activity_at)}</td>
                    <td>{util.formatTimestamp(parent.created)}</td>
                    <td>{parent.first_moment_id}</td>
                    <td>{parent.last_moment_id}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {hasChildren && (
        <Card title="Child Sessions">
          <Table className="mb-4">
            <thead>
              <tr>
                <th>Child Session ID</th>
                <th>Last Activity</th>
                <th>Created</th>
                <th>Origin Moment</th>
              </tr>
            </thead>
            <tbody>
              {sessionChildren?.children?.map((child) => (
                <ChildSessionRow
                  key={child.session_id}
                  child={child}
                  commonAPI={commonAPI}
                />
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </CardGrid>
  );
};

export default SessionCloneHistory;
